import { applicantSection, feeInfo, feeProofAttachment, otherAttachments, replySection } from "modules/ebiForms/common";
import { otherPersonFields } from "modules/ebiForms/definitions/sharedFamilyLabour";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * H4. Wniosek o wydanie karty parkingowej — art. 8 ust. 3a–7 Prawa o ruchu drogowym; tryb: § 2–8 rozporządzenia MPiPS
 * z 25.06.2014 r. w sprawie wzoru oraz trybu wydawania i zwrotu kart parkingowych (tekst z
 * api.sejm.gov.pl/eli/acts/DU/2022/1611 pobrany 2026-10-22); opłata 21 zł — rozporządzenie MPiPS z 24.06.2014 r. Układ jak
 * wzór ogólnopolski (zał. nr 1 do rozporządzenia) z karty gov.pl — https://www.gov.pl/attachment/5e49e671-2aa6-4668-bdbb-4f1ddbd3364a
 * (pobrany 2026-10-22): część A (osoba niepełnosprawna, osoba składająca wniosek, oświadczenia, upoważnienie do odbioru),
 * część B (placówka). Wniosek podpisuje się osobiście w obecności przewodniczącego zespołu i składa na nim wzór podpisu
 * (§ 3–4), pełnomocnik jest niedopuszczalny (§ 2 ust. 3) — formularz EBI jest wstępnym zgłoszeniem, bez części
 * reprezentacji. Papierowej fotografii nie zastąpi skan — załącznik ze zdjęciem jest poglądowy i nieobowiązkowy.
 */
const PERSON: EbiCondition = { field: "wnioskodawcaKarty", equals: "osoba" };
const FACILITY: EbiCondition = { field: "wnioskodawcaKarty", equals: "placowka" };
const REPRESENTATIVE: EbiCondition = { all: [PERSON, { field: "skladajacy", in: ["rodzic", "opiekun", "kurator"] }] };
const AUTHORIZED: EbiCondition = { all: [PERSON, { field: "upowaznienieOdbioru", equals: true }] };
const by = (role: string): EbiCondition => ({ all: [PERSON, { field: "skladajacy", equals: role }] });

const H4: IEbiFormDefinition = {
  code: "H4",
  version: 1,
  title: "Wniosek o wydanie karty parkingowej",
  category: EEbiFormCategory.Disability,
  office: EEbiFormOffice.CountyUnit,
  description: "Karta parkingowa dla osoby niepełnosprawnej albo placówki — wstępne zgłoszenie; wniosek podpisuje się osobiście w zespole do spraw orzekania.",
  keywords: ["karta parkingowa", "niepełnosprawność", "parkowanie", "koperta", "zespół orzekający", "placówka"],
  organ: "Przewodniczący powiatowego (miejskiego) zespołu do spraw orzekania o niepełnosprawności — wybranego przez osobę niepełnosprawną; placówka — właściwego według siedziby",
  legalBasis:
    "art. 8 ust. 3a–7 ustawy z dnia 20 czerwca 1997 r. — Prawo o ruchu drogowym; § 2–8 rozporządzenia Ministra Pracy i Polityki Społecznej z dnia 25 czerwca 2014 r. w sprawie wzoru oraz trybu wydawania i zwrotu kart parkingowych; rozporządzenie Ministra Pracy i Polityki Społecznej z dnia 24 czerwca 2014 r. w sprawie wysokości opłaty za wydanie karty parkingowej",
  request: "Wnoszę o wydanie karty parkingowej.",
  feeLabel: "21 zł",
  fee: "21 zł za wydanie karty parkingowej (bez opłaty skarbowej).",
  deadlineLabel: "do 30 dni",
  deadline:
    "Do 30 dni od złożenia wniosku spełniającego wymogi formalne zespół informuje o terminie i miejscu odbioru karty albo o odmowie (§ 8 rozporządzenia).",
  variantField: "wnioskodawcaKarty",
  variants: {
    osoba: { title: "Wniosek o wydanie karty parkingowej dla osoby niepełnosprawnej" },
    placowka: { title: "Wniosek o wydanie karty parkingowej dla placówki" },
  },
  sections: [
    {
      code: "karta",
      title: "Wniosek o wydanie karty parkingowej",
      fields: [
        feeInfo(
          "infoOsobiscie",
          "Wniosek o kartę parkingową podpisuje się osobiście w obecności przewodniczącego zespołu do spraw orzekania o niepełnosprawności albo osoby przez niego upoważnionej; osoba niepełnosprawna składa na druku wniosku wzór podpisu i dołącza papierową fotografię 35 × 45 mm. Wniosek złożony w Elektronicznym Biurze Interesanta jest wstępnym zgłoszeniem — zespół wyznaczy termin podpisania wniosku i okazania oryginału orzeczenia. Wniosku nie może złożyć pełnomocnik; za osobę, która nie ukończyła 18 lat albo jest ubezwłasnowolniona, wniosek składa rodzic, opiekun albo kurator.",
        ),
        {
          code: "wnioskodawcaKarty",
          type: "radio",
          label: "Wniosek dotyczy",
          required: true,
          options: [
            { value: "osoba", label: "osoby niepełnosprawnej (część A wzoru)" },
            {
              value: "placowka",
              label:
                "placówki zajmującej się opieką, rehabilitacją lub edukacją osób niepełnosprawnych mających znacznie ograniczone możliwości samodzielnego poruszania się (część B wzoru)",
            },
          ],
        },
      ],
    },
    applicantSection("Osoba składająca wniosek"),
    {
      code: "czescA",
      title: "Część A — osoba niepełnosprawna",
      visibleWhen: PERSON,
      fields: [
        {
          code: "skladajacy",
          type: "radio",
          label: "Składam wniosek",
          required: true,
          defaultValue: "osoba",
          options: [
            { value: "osoba", label: "we własnym imieniu — jestem osobą niepełnosprawną" },
            { value: "rodzic", label: "jako rodzic sprawujący władzę rodzicielską nad osobą niepełnosprawną" },
            { value: "opiekun", label: "jako opiekun osoby niepełnosprawnej" },
            { value: "kurator", label: "jako kurator osoby niepełnosprawnej" },
          ],
        },
        ...otherPersonFields({
          prefix: "osoba",
          who: "osoby niepełnosprawnej",
          visibleWhen: REPRESENTATIVE,
          addressLabel: "Adres do korespondencji osoby niepełnosprawnej",
          sameAddressLabel: "Osoba niepełnosprawna ma ten sam adres do korespondencji co składający wniosek",
        }),
        {
          code: "orzeczenieNumer",
          type: "text",
          label: "Numer orzeczenia potwierdzającego niepełnosprawność",
          required: true,
          width: "half",
          maxLength: 100,
          hint: "Orzeczenie ze wskazaniem, o którym mowa w art. 6b ust. 3 pkt 9 ustawy o rehabilitacji — oryginał okazujesz przy podpisaniu wniosku.",
        },
        { code: "orzeczenieData", type: "date", label: "Data wydania orzeczenia", required: true, width: "half" },
        { code: "upowaznienieOdbioru", type: "checkbox", label: "Upoważniam inną osobę do odbioru w moim imieniu karty parkingowej" },
        {
          code: "upowaznionyImieNazwisko",
          type: "text",
          label: "Imię i nazwisko osoby upoważnionej do odbioru",
          required: true,
          visibleWhen: AUTHORIZED,
          width: "half",
          maxLength: 200,
        },
        { code: "upowaznionyPesel", type: "pesel", label: "PESEL osoby upoważnionej do odbioru", required: true, visibleWhen: AUTHORIZED, width: "half" },
      ],
    },
    {
      code: "czescB",
      title: "Część B — placówka",
      visibleWhen: FACILITY,
      fields: [
        { code: "placowkaNazwa", type: "text", label: "Nazwa placówki", required: true, maxLength: 300 },
        { code: "placowkaKrs", type: "text", label: "Numer KRS", width: "half", maxLength: 10 },
        { code: "placowkaRegon", type: "text", label: "Numer REGON", required: true, width: "half", maxLength: 14 },
        { code: "pojazdNumer", type: "text", label: "Numer rejestracyjny pojazdu placówki", required: true, width: "half", maxLength: 20 },
        { code: "pojazdMarka", type: "text", label: "Marka i model pojazdu placówki", required: true, width: "half", maxLength: 100 },
        { code: "placowkaAdres", type: "text", label: "Adres siedziby — ulica, numer", required: true, maxLength: 200 },
        { code: "placowkaKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, width: "third", placeholder: "00-000" },
        { code: "placowkaMiejscowosc", type: "text", label: "Miejscowość", required: true, width: "twoThirds", maxLength: 100 },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "oswWladza",
          type: "declaration",
          label: "Oświadczam, że sprawuję władzę rodzicielską nad osobą niepełnosprawną, która jest uprawniona do uzyskania karty parkingowej.",
          required: true,
          visibleWhen: by("rodzic"),
        },
        {
          code: "oswOpieka",
          type: "declaration",
          label: "Oświadczam, że sprawuję opiekę nad osobą niepełnosprawną, która jest uprawniona do uzyskania karty parkingowej.",
          required: true,
          visibleWhen: by("opiekun"),
        },
        {
          code: "oswKuratela",
          type: "declaration",
          label: "Oświadczam, że sprawuję kuratelę nad osobą niepełnosprawną, która jest uprawniona do uzyskania karty parkingowej.",
          required: true,
          visibleWhen: by("kurator"),
        },
        {
          code: "oswUpowaznienie",
          type: "declaration",
          label: "Oświadczam, że posiadam upoważnienie do reprezentowania placówki uprawnionej do uzyskania karty parkingowej.",
          required: true,
          visibleWhen: FACILITY,
        },
        {
          code: "oswDzialalnosc",
          type: "declaration",
          label:
            "Oświadczam, że placówka prowadzi działalność w zakresie opieki, rehabilitacji lub edukacji osób niepełnosprawnych mających znacznie ograniczone możliwości samodzielnego poruszania się i jest uprawniona do uzyskania karty parkingowej.",
          required: true,
          visibleWhen: FACILITY,
        },
        {
          code: "oswAktualne",
          type: "declaration",
          label: "Oświadczam, że dane podane we wniosku są aktualne i zgodne ze stanem faktycznym.",
          required: true,
        },
      ],
    },
    {
      code: "oplata",
      title: "Opłata",
      fields: [
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata za wydanie karty parkingowej",
          feeAmount: 21,
          hint: "Wpłać 21 zł na rachunek wskazany przez zespół lub starostwo (BIP) i dołącz potwierdzenie przelewu. Opłata ewidencyjna wymieniona we wzorze wniosku w praktyce nie jest pobierana.",
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    feeProofAttachment("Dowód uiszczenia opłaty za wydanie karty parkingowej (21 zł)"),
    {
      code: "fotografia",
      label: "Fotografia 35 × 45 mm (skan — poglądowo)",
      hint: "Do wniosku dołącza się papierową fotografię z aktualnym wizerunkiem, bez nakrycia głowy i okularów z ciemnymi szkłami (wyjątki: wada wzroku, nakrycie głowy zgodne z wyznaniem, jeśli takie zdjęcie jest w dokumencie tożsamości). Oryginał przyniesiesz przy podpisaniu wniosku.",
      visibleWhen: PERSON,
    },
    {
      code: "orzeczenieKopia",
      label: "Kopia orzeczenia ze wskazaniem do karty parkingowej (art. 6b ust. 3 pkt 9 ustawy o rehabilitacji)",
      hint: "Oryginał prawomocnego orzeczenia okazujesz do wglądu przy podpisaniu wniosku (§ 5 ust. 4 rozporządzenia).",
      visibleWhen: PERSON,
    },
    {
      code: "postanowienieSadu",
      label: "Postanowienie sądu o ustanowieniu opieki lub kurateli",
      hint: "Zespoły zwykle proszą o okazanie postanowienia do wglądu.",
      visibleWhen: { all: [PERSON, { field: "skladajacy", in: ["opiekun", "kurator"] }] },
    },
    {
      code: "dowodRejestracyjny",
      label: "Dowód rejestracyjny pojazdu placówki",
      hint: "Do wglądu przy złożeniu wniosku (§ 6 ust. 3 rozporządzenia).",
      visibleWhen: FACILITY,
    },
    otherAttachments(),
  ],
};

export default H4;
